import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { FiltersFacade } from '@modules/filters/facades';
import { defaultFloorFormValue } from '@modules/filters/constants';
import { FilterTypeEnum, IntervalEnum } from '@modules/filters/enums';
import { BuildingsFacade } from '@modules/buildings/facades/buildings-facade.service';
import { Building, Floor } from '@modules/buildings/models';
import { datePickerDateFormat } from '@shared/constants';
import { getDatesDiff } from '@shared/utils/date-utils';
import { LabelPosition, Size } from '@usitsdasdesign/dds-ng/shared';
import {
  SelectItemOptions,
  SelectOptions,
} from '@usitsdasdesign/dds-ng/select';
import {
  dateRangeCustomValidator,
  DateRangeIntervalValue,
} from './date-range-validator';
import { intervalCustomValidator } from './interval-validator';
import { ModalService } from '@core/services';

type RestrictedInterval = Exclude<IntervalEnum, 3 | 4 | 5>;

const intervalDaysAmountBoundaries: Record<RestrictedInterval, number> = {
  [IntervalEnum.TenMinutes]: 31,
  [IntervalEnum.Hour]: 186,
};

const exceedDaysNumberErrorMessages: Record<RestrictedInterval, string> = {
  [IntervalEnum.TenMinutes]: '10 min interval is available for one month only',
  [IntervalEnum.Hour]: 'hour interval is available for 6 months only',
};

@Component({
  standalone: false,
  selector: 'hm-filters-panel-floor',
  templateUrl: './filters-panel-floor.component.html',
  styleUrls: ['./filters-panel-floor.component.scss'],
})
export class FiltersPanelFloorComponent implements OnInit, AfterViewInit {
  private readonly buildingsFacade = inject(BuildingsFacade);
  private readonly filtersFacade = inject(FiltersFacade);
  private readonly modalService = inject(ModalService);
  public isError = false;
  public readonly vm$ = combineLatest([
    this.buildingsFacade.buildingsData$,
    this.filtersFacade.getFilterFormValueByType(
      FilterTypeEnum.FloorUtilization,
    ),
  ]).pipe(
    map(([buildingsData, filterValue]) => ({ buildingsData, filterValue })),
  );

  public readonly datePickerDateFormat = datePickerDateFormat;
  public readonly today = new Date();
  public readonly defaultFormValue = defaultFloorFormValue;
  public filteredFloors: Floor[] = [];
  protected hoursTypesOptions: SelectItemOptions[] = [];
  protected buildingsDataIntervals: SelectItemOptions[] = [];

  selectOptions: SelectOptions = {
    labelPosition: LabelPosition.external,
    size: Size.md,
    customClass: 'add-floor__form-hours-type',
  };

  form: FormGroup = new FormGroup({
    building: new FormControl(null),
    floor: new FormControl(null),
    interval: new FormControl(null, [intervalCustomValidator('dateRange')]),
    dateRange: new FormControl(null, { updateOn: 'blur' }),
    hoursType: new FormControl(null),
    device: new FormControl(null),
  });

  ngAfterViewInit(): void {
    if (!this.form.get('building')?.value) {
      this.form.get('floor')?.disable();
    }
    if (!this.form.get('floor')?.value) {
      this.form.get('dateRange')?.disable();
    }
  }

  ngOnInit(): void {
    this.form.get('building')?.valueChanges.subscribe((building) => {
      if (building) {
        this.form.get('floor')?.enable();
      } else {
        this.form.get('floor')?.disable();
      }
    });
    this.form.get('floor')?.valueChanges.subscribe((floor) => {
      if (floor) {
        this.form.get('dateRange')?.enable();
        this.form.get('dateRange')?.reset();
      } else {
        this.form.get('dateRange')?.reset();
        this.form.get('dateRange')?.disable();
      }
    });
    this.form.get('interval')?.valueChanges.subscribe(() => {
      this.form.get('dateRange')?.reset();
    });
    this.vm$
      .pipe(
        filter((vm) => !!vm.buildingsData && !!vm.filterValue),
        take(1),
      )
      .subscribe((vm) => {
        this.form.patchValue(vm.filterValue);

        const dateRangeControl = this.form.get('dateRange');
        dateRangeControl?.markAsPristine();
        dateRangeControl?.markAsUntouched();
        dateRangeControl?.updateValueAndValidity();

        if (!dateRangeControl?.touched) {
          this.form.get('dateRange')?.setErrors(null);
          this.form.get('dateRange')?.updateValueAndValidity();
        }

        this.hoursTypesOptions = vm.buildingsData.hoursTypes.map((type) => ({
          heading: type.label,
          value: type.value,
          customClass: this.selectOptions.customClass,
          active: type.value === vm.filterValue.hoursType?.value,
        }));

        this.buildingsDataIntervals = vm.buildingsData.intervals.map(
          (type) => ({
            heading: type.label,
            value: type.value,
            customClass: this.selectOptions.customClass,
            isDefault: type.value === vm.filterValue.interval?.value,
          }),
        );

        this.filterFloors(vm.filterValue.building);

        this.form
          .get('hoursType')
          ?.setValue(vm.filterValue.hoursType.value, { emitEvent: true });
        this.form.get('interval')?.setValue(vm.filterValue.interval.value);
        Promise.resolve().then(() => {
          this.form.get('dateRange')?.setValue(vm.filterValue.dateRange);
        });
      });

    const intervalControl = this.form.get('interval');
    const dateRangeControl = this.form.get('dateRange');

    intervalControl?.valueChanges.subscribe(
      (intervalValue: DateRangeIntervalValue) => {
        if (dateRangeControl) {
          dateRangeControl.setValidators([
            dateRangeCustomValidator(intervalValue),
          ]);
          dateRangeControl.updateValueAndValidity({ emitEvent: false });
        }
      },
    );

    dateRangeControl?.valueChanges.subscribe(() => {
      this.form.get('interval')?.updateValueAndValidity({ emitEvent: false });

      dateRangeControl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  selectBuilding(): void {
    const building = this.form.get('building')?.value;
    this.filterFloors(building);
    this.form.get('floor')?.reset();
    this.form.get('dateRange')?.reset();
    const dateRangeControl = this.form.get('dateRange');

    if (!building) {
      dateRangeControl?.clearValidators();
    } else {
      dateRangeControl?.setValidators([
        dateRangeCustomValidator(this.form.get('interval')?.value),
      ]);
    }

    this.form.get('interval')?.updateValueAndValidity({ emitEvent: false });
    dateRangeControl?.updateValueAndValidity({ emitEvent: false });
  }

  selectFloor(): void {
    this.form.get('interval')?.setValue(this.defaultFormValue.interval.value);
    const dateRangeControl = this.form.get('dateRange');

    if (this.form.get('floor')?.value) {
      dateRangeControl?.setValidators([
        dateRangeCustomValidator(this.form.get('interval')?.value),
      ]);
    } else {
      dateRangeControl?.clearValidators();
    }
    dateRangeControl?.updateValueAndValidity();
  }

  selectInterval(): void {
    this.form.get('dateRange')?.reset();
  }

  selectDateRange(event: Date[]): void {
    const intervalValue = this.form.get('interval')?.value;

    if (!event?.length || !intervalValue) {
      return;
    }

    const from = event[0];
    const to = event[1];
    const daysDiff =
      getDatesDiff(from.toUTCString(), to.toUTCString(), 'days') + 1;
    const interval = intervalValue.value ? intervalValue.value : intervalValue;
    if (
      interval in intervalDaysAmountBoundaries &&
      daysDiff > intervalDaysAmountBoundaries[interval as RestrictedInterval]
    ) {
      this.form.controls.dateRange.setErrors({
        maxDays: exceedDaysNumberErrorMessages[interval as RestrictedInterval],
      });
      this.isError = true;
    } else {
      this.form.get('dateRange')?.setErrors(null);
      this.isError = false;
    }
  }

  filterFloors(building: Building | null): void {
    if (!building) {
      this.filteredFloors = [];
      return;
    }

    this.filteredFloors = this.buildingsFacade.filterFloors(
      building.buildingId,
    );
  }

  submit(): void {
    const dateRange = this.form.get('dateRange');
    dateRange?.setValidators([Validators.required]);
    dateRange?.updateValueAndValidity();
    if (this.form.invalid) {
      return;
    }
    const filter = this.filtersFacade.convertFormValueToModel(
      FilterTypeEnum.FloorUtilization,
      this.form.value,
    );

    this.filtersFacade.applyFilter(filter);
    dateRange?.setErrors(null);
    this.modalService.close();
  }

  buildDateRangeErrorMessage(): string | null {
    const errors = this.form.get('dateRange')?.errors;
    if (!errors) {
      return null;
    }

    const customErrors = errors.error;
    const maxDaysError = errors.maxDays;

    let msg = '';
    if (customErrors?.startDate) {
      msg += customErrors.startDate + ' ';
    }
    if (customErrors?.endDate) {
      msg += customErrors.endDate + ' ';
    }
    if (maxDaysError) {
      msg += maxDaysError;
    }

    return msg.trim() || null;
  }

  buildIntervalErrorMessage(): string | null {
    const errors = this.form.get('interval')?.errors;

    if (!errors) {
      return null;
    }

    let msg = '';

    if (errors.maxDays) {
      msg += errors.maxDays;
    }

    return msg || null;
  }
}
